import { useEffect, useRef, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';

import {
  parseLaundryProducts,
  formatValuesIntoArray,
  getUpdatedLaundryBundle,
  getBundleAccessories,
  getBundleDetails
} from '../utils/product-utils';
import { useDataProducts } from './useDataProducts';
import { NONE_VALUE } from '../constants/index';

export const useLaundryEntryPoint = ({ bundle }) => {
  const isLaundryEntryPointEnabled = useConfigService('fs:isLaundryEntryPointEnabled') || false;
  const isVariantSwap = useRef(false);
  const updateProductRef = useRef(null);
  const [productsState, setProductsState] = useState(null);
  const [productIds, setProductIds] = useState(null);
  const configSelectableVariant = formatValuesIntoArray(useConfigService('selectableVariant') || null);
  const productsCacheRef = useRef({});

  // TODO: we need to update the accessory data, once the contract is defined for accessories
  const updatedBundle = getUpdatedLaundryBundle(bundle, configSelectableVariant);

  const bundleProductIds = updatedBundle?.bundleSpecificationDetails?.components?.map(
    ({ defaultProductId }) => defaultProductId
  ) || [];

  const bundleDetailsRef = useRef(getBundleDetails(updatedBundle?.bundleSpecificationDetails));
  const bundleAccessories = getBundleAccessories(updatedBundle?.bundleSpecificationDetails);
  const accessoryProductIds = bundleAccessories?.[0]?.products?.map(({ defaultProductId }) => defaultProductId) || [];

  const initialProductIds = [...bundleProductIds, ...accessoryProductIds];
  const itemIds = productIds || initialProductIds;
  const skip = !isLaundryEntryPointEnabled || !itemIds || itemIds.length === 0;

  const { data, loading, error } = useDataProducts({
    itemIds,
    skip
  });

  const saveProductsInCache = (products) => {
    products.forEach((product) => {
      productsCacheRef.current[product.itemId] = product;
    });
  };

  const getProductFromCache = (productId) => {
    return productsCacheRef.current[productId];
  };

  const updateBundleProducts = ({ newProduct, currentItemId }) => {
    const updatedProducts = productsState?.products?.map((product) => {
      if (product.itemId === currentItemId) {
        return {
          ...newProduct,
          selectedVariant: newProduct?.itemId,
          selectableVariants: product?.selectableVariants || []
        };
      }
      return product;
    });

    const newState = {
      ...productsState,
      products: updatedProducts || []
    };

    setProductsState(newState);
  };

  const updateAccessories = ({ newProducts, offeringSelected }) => {
    const newState = {
      ...productsState,
      accessories: {
        ...productsState?.accessories,
        products: newProducts,
        selectedVariant: offeringSelected
      }
    };

    setProductsState(newState);
  };

  const swapProduct = ({ currentItemId, newItemId }) => {
    isVariantSwap.current = true;
    const cachedProduct = getProductFromCache(newItemId);
    if (cachedProduct) {
      updateBundleProducts({
        newProduct: cachedProduct,
        currentItemId
      });
      return;
    }

    updateProductRef.current = { currentItemId, isAccessory: false };
    setProductIds([newItemId]);
  };

  const swapAccessory = ({ offeringName }) => {
    isVariantSwap.current = true;
    if (offeringName === NONE_VALUE) {
      const newState = {
        ...productsState,
        accessories: {
          ...productsState?.accessories,
          selectedVariant: NONE_VALUE
        }
      };
      setProductsState(newState);
      return;
    }
    const accessoryOffering = bundleAccessories.find((offering) => offering.offeringName === offeringName);
    if (!accessoryOffering) {
      return;
    }

    const newItemIds = accessoryOffering?.products?.map(({ defaultProductId }) => defaultProductId) || [];
    const cachedProducts = newItemIds.map(getProductFromCache).filter(Boolean);
    if (cachedProducts.length === newItemIds.length) {
      updateAccessories({
        newProducts: cachedProducts,
        offeringSelected: offeringName
      });
      return;
    }

    updateProductRef.current = { currentOfferingName: offeringName, isAccessory: true };
    setProductIds(newItemIds);
  };

  useEffect(() => {
    if (!data?.products) {
      return;
    }
    try {
      const productDetails = bundleDetailsRef.current?.products;
      const updatedProducts = data?.products?.map((product) => ({
        ...product,
        quantity: productDetails[product?.itemId]?.quantity || 1,
        features: {
          directDeliveryCartMethod: true
        }
      }));

      if (updateProductRef.current) {
        const newProduct = {
          ...updatedProducts?.[0]
        };

        if (updateProductRef.current.isAccessory) {
          updateAccessories({
            newProducts: updatedProducts,
            offeringSelected: updateProductRef.current.currentOfferingName
          });
        } else {
          updateBundleProducts({
            newProduct,
            currentItemId: updateProductRef.current.currentItemId
          });
        }
        updateProductRef.current = null;
      } else {
        setProductsState(parseLaundryProducts(updatedProducts, bundleDetailsRef.current));
      }
      saveProductsInCache(updatedProducts);
    } catch (errorProducts) {
      setProductsState({});
    }

  }, [data]);

  const { products, accessories } = productsState || {};
  return {
    products: products || [],
    accessories: accessories || [],
    error,
    loading: !skip && (loading || productsState == null) && !error && !isVariantSwap?.current,

    // methods
    swapProduct,
    swapAccessory
  };
};